import { graphql, useLazyLoadQuery } from "react-relay";

import {
    ContentPlatformContentClause,
    ContentPlatformContentQueryInput,
    templateInitialSearchResultsRelayQuery
} from "@/__generated__/templateInitialSearchResultsRelayQuery.graphql";

import { CategoryTemplateOrder } from "../../config/category-template-order";
import {
    DEFAULT_CATEGORY_SLUG,
    parseCategorySlug
} from "../hooks/use-category-slug";

import { CategorySearchSuggestion } from "./template-category-search-results-relay";
import { TemplateSearchSuggestion } from "./template-fuzzy-search-results-relay";
import { TemplatePaginatedSearchResults } from "./template-paginated-search-results-relay";

export const TemplateInitialSearchResultsRelayQuery = graphql`
    query templateInitialSearchResultsRelayQuery(
        $searchQuery: ContentPlatformContentQueryInput!
    ) {
        ...templatePaginatedSearchResultsRelayRefetchableFragment
            @alias(as: "initialSearchResults")
            @arguments(searchQuery: $searchQuery)
    }
`;

type TemplateId = string;

function buildWhereClause(
    value?:
        | TemplateSearchSuggestion
        | CategorySearchSuggestion
        | TemplateId[]
        | null
): ContentPlatformContentClause {
    const filters: ContentPlatformContentClause[] = [];

    if (Array.isArray(value) && value.length < 1) {
        // An empty array of templateIds indicates that we received no recomendations
        // from the service, so we should fall back to showing the default category.
        value = null;
    }

    if (Array.isArray(value)) {
        filters.push({
            fieldNamed: "templateId",
            havingValues: value
        });
    } else if (value?.type === "template" && value.id) {
        filters.push({ fieldNamed: "urlSlug", havingValues: [value.id] });
    } else {
        const categorySlug =
            value?.type === "category" ? value.id : DEFAULT_CATEGORY_SLUG;
        const orderedTemplates =
            CategoryTemplateOrder[
                categorySlug as keyof typeof CategoryTemplateOrder
            ];
        if (orderedTemplates) {
            filters.push({
                fieldNamed: "urlSlug",
                havingValues: Array.from(Object.keys(orderedTemplates))
            });
        } else {
            filters.push({
                fieldNamed: "category.urlSlug",
                havingValues: [categorySlug]
            });
        }
    }

    return filters.length > 1 ? { and: filters } : filters[0] ?? {};
}

function isLocation(
    value:
        | string
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | TemplateId[]
        | Location
        | null
): value is Location {
    return (
        value != null &&
        typeof value !== "string" &&
        !Array.isArray(value) &&
        !(value as CategorySearchSuggestion).type
    );
}

export function parseSearchQuery(
    searchTerm:
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | TemplateId[]
        | Location
        | null,
    withQuery?: Partial<Exclude<ContentPlatformContentQueryInput, "where">>
): ContentPlatformContentQueryInput {
    let whereClause: ContentPlatformContentClause;
    if (isLocation(searchTerm)) {
        const id = parseCategorySlug(searchTerm.pathname);
        whereClause = buildWhereClause(id ? { type: "category", id } : null);
    } else {
        whereClause = buildWhereClause(searchTerm);
    }
    let first = 9;

    if (withQuery?.first == null) {
        // if the whereClause has conditions that amount to more than `first`,
        // we need to set the first to the necessary count to fullfill the where clause.
        if (Array.isArray(whereClause.havingValues)) {
            first = Math.max(first, whereClause.havingValues.length);
        } else if (whereClause.and) {
            first = whereClause.and.reduce((acc, clause) => {
                if (clause.havingValues) {
                    return Math.max(acc, clause.havingValues.length);
                }
                return acc;
            }, first);
        }
    }

    return {
        first,
        ...withQuery,
        where: whereClause
    };
}

type SortLookup = {
    [templateUrlSlug: string]: number;
};

export type TemplateInitialSearchResultsProps = {
    searchQuery: ContentPlatformContentQueryInput;
    /**
     * A unique key to identify the search term.
     * This is required to force a new instance
     * of `TemplatePaginatedSearchResults`
     * when the search term changes.
     */
    searchKey: string;
    sortLookup?: "name" | SortLookup;
};

export const TemplateInitialSearchResults = ({
    searchQuery,
    searchKey,
    sortLookup
}: TemplateInitialSearchResultsProps) => {
    const { initialSearchResults } =
        useLazyLoadQuery<templateInitialSearchResultsRelayQuery>(
            TemplateInitialSearchResultsRelayQuery,
            { searchQuery }
        );

    return (
        <TemplatePaginatedSearchResults
            key={searchKey}
            initialSearchResults={initialSearchResults}
            searchQuery={searchQuery}
            sortLookup={sortLookup}
        />
    );
};
